import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PERMISSIONS, Task } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getVerifyPermission } from '@repo/shared/helpers';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import { DataGrid, DataGridColumn } from '@repo/components';
import { TaskStatusChip } from '@repo/components';
import { getPartnerCompanyApprovals } from 'src/api/partner';
import { useBreadcrumbLevel } from '@repo/components/hooks';

type ApprovalSearchSortOptions = 'validTo' | 'updatedAt' | 'createdAt' | 'type' | 'description';
type SortDirection = 'asc' | 'desc';
type ApprovalSearchSort = `${ApprovalSearchSortOptions}:${SortDirection}`;

type ApprovalSearch = {
	sort: ApprovalSearchSort;
	page?: number;
};

export const Route = createFileRoute('/partner_/$partnerId/approvals/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>): ApprovalSearch => {
		// validate and parse the search params into a typed state
		return {
			sort: (typeof search.sort === 'string' ? (search.sort as ApprovalSearchSort) : undefined) || 'updatedAt:desc',
			page: (search.page as number | undefined) || 0,
		};
	},
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId } = Route.useParams();
	useBreadcrumbLevel(1, 'Approvals');
	const { sort } = Route.useSearch();
	const queryClient = useQueryClient();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const navigate = useNavigate();
	const updateQueryParams = useUpdateQueryParams();

	const {
		data: approvalsResponse,
		isLoading: approvalsLoading,
		error: _getApprovalsError,
	} = useQuery({
		queryKey: ['partner-company-approvals', partnerId],
		queryFn: () => getPartnerCompanyApprovals(partnerId),
		enabled: !userLoading && !!authenticatedUser,
	});
	const approvals = approvalsResponse?.data || [];

	if (!verifyPermission(PERMISSIONS.partnerCompanyEventsView)) {
		navigate({ to: `/partner/${partnerId}` });
		return null;
	}

	if (userLoading || approvalsLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	const handleSort = ([sort]) => {
		updateQueryParams({ sort: sortToString(sort) });
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page });
	};

	const handleRowClick = ({ row }) => {
		navigate({ to: `/partner/${row.groupId}/approvals/${row.id}` });
		return null;
	};

	const columns: DataGridColumn<Task>[] = [
		{
			headerName: 'Description',
			field: 'name',
			flex: 1,
		},
		{
			field: 'type',
			headerName: 'Type',
			width: 100,
			renderCell: (c) => c.value && `${c.value[0].toUpperCase()}${c.value.slice(1).toLowerCase()}`,
		},
		{
			field: 'createdAt',
			headerName: 'Assigned',
			width: 120,
			valueFormatter: (params) => new Date(params.value).toLocaleDateString('en-GB'),
		},
		{
			field: 'updatedAt',
			headerName: 'Last updated',
			width: 120,
			valueFormatter: (params) => new Date(params.value).toLocaleDateString('en-GB'),
		},
		{
			field: 'validTo',
			headerName: 'Valid to',
			width: 100,
			renderCell: (c) => {
				const value = c.row.validToOverride ? c.row.validToOverride : c.row.validTo;
				return value ? new Date(value).toLocaleDateString('en-GB') : null;
			},
		},
		{
			field: 'status',
			headerName: 'Status',
			renderCell: (cell) => <TaskStatusChip status={cell.row.status} />,
			sortable: false,
			width: 175,
		},
	];

	return (
		<div className="p-6">
			<div className="mt-2  border border-gray-300 rounded bg-white shadow-sm">
				<DataGrid
					data={approvals || []}
					columns={columns}
					// isLoading={isLoading}
					rowCount={approvalsResponse?.count || 0}
					paginationMode="server"
					sortingMode="server"
					onPaginationModelChange={handlePaginationModelChange}
					onSortModelChange={handleSort}
					sortingOrder={['asc', 'desc']}
					sortModel={[stringToSort(sort || 'startTime:desc')]}
					pageSize={approvalsResponse?.pageSize}
					pageSizeOptions={[approvalsResponse?.pageSize || 0]}
					page={approvalsResponse?.page}
					onRowClick={handleRowClick}
					pagination
					getRowClassName={(p) => p.row.markedForReview && 'error'}
					onReload={() => queryClient.invalidateQueries({ queryKey: ['partner-company-approvals', partnerId] })}
				/>
			</div>
		</div>
	);
}
